import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelSmall} from "@khanacademy/wonder-blocks-typography";

import {Section} from "../components/section/section.tsx";

import EmailSubscriptionSwitch from "./email-subscription-switch.tsx";
import type {EmailSubscriptions} from "./types.ts";

interface Props {
    hasChildren: boolean;
    childrenList: EmailSubscriptions["parentReportEmailSubscriptions"];
}

/**
 * A component to manage email settings for activity reports.
 */
export default function EmailReportsSection({
    hasChildren,
    childrenList,
}: Props): React.ReactElement {
    const [showError, setShowError] = React.useState(false);

    const children = (
        <View style={{gap: sizing.size_120}}>
            <Body>
                {t`Receive a summary of what your children have been working on:`}
            </Body>

            {(childrenList ?? []).map((child) => (
                <React.Fragment key={child.key}>
                    <EmailSubscriptionSwitch
                        checked={child.checked}
                        label={child.description}
                        subscriptionKey={child.key}
                        showError={() => setShowError(true)}
                        clearError={() => setShowError(false)}
                        inputId={"childNotification" + child.key}
                    />
                </React.Fragment>
            ))}
        </View>
    );

    return (
        <Section
            legendContent={t`Weekly activity reports`}
            children={
                <View style={{gap: sizing.size_160}}>
                    {hasChildren && children}
                    {showError && (
                        <LabelSmall style={styles.errorText}>
                            {t`Oops! Something went wrong. Please try again.`}
                        </LabelSmall>
                    )}
                </View>
            }
        />
    );
}

const styles = StyleSheet.create({
    errorText: {
        color: semanticColor.status.critical.foreground,
        marginBlockStart: sizing.size_040,
    },
});
